import { Component, inject, OnInit } from '@angular/core';
import { GungSettingsBaseViewComponent } from '../../gung-settings-base-view/gung-settings-base-view.component';
import { ConfigurationsService, GungConfiguration } from '../../../services/configurations.service';
import { Observable, first, forkJoin, of, switchMap, tap } from 'rxjs';
import { GungNotificationService, OptionsListMulti, checkObjectEquals } from 'gung-common';
import { GungFlowService } from '../../../services/gung-flow/gung-flow.service';
import { SystemSettings } from '../../../models/system-setting';

@Component({
    selector: 'lib-gung-settings-users-view',
    templateUrl: './gung-settings-users-view.component.html',
    styleUrls: ['./gung-settings-users-view.component.css'],
    standalone: false
})
export class GungSettingsUsersViewComponent extends GungSettingsBaseViewComponent implements OnInit {
  protected systemSettings: SystemSettings;
  protected configurations: { [s: string]: GungConfiguration } = {};
  public newConfigurations: { [s: string]: GungConfiguration } = {};

  public availableUserGroupOptions: OptionsListMulti[] = [];

  /** Working copy of system.extra.userGroups; the master list of selectable groups. */
  protected newUserGroups: string[] = [];

  protected gungNotificationService = inject(GungNotificationService);

  constructor(
    protected configurationsService: ConfigurationsService,
    protected gungFlowService: GungFlowService
  ) {
    super();
  }

  ngOnInit(): void {
    this.configurationsService.getConfigurationsByIds(['NewUserDefaultsConfig']).pipe(
      first(),
      switchMap(configs => forkJoin({
        configs: of(configs),
        systemSettings: this.gungFlowService.getSystemGung().pipe(first())
      }))
    ).subscribe(({ configs, systemSettings }) => {
      for (const config of configs) {
        this.configurations[config.id] = config;
      }
      this.systemSettings = systemSettings;
      this.reset();
    });
  }

  public reset(): void {
    this.newConfigurations = structuredClone(this.configurations);
    this.newUserGroups = [...(this.systemSettings?.extra?.userGroups ?? [])];
    this.rebuildUserGroupOptions();
    this.checkForChanges();
  }

  /** Rebuild the "default active user groups" picker from the current working group list. */
  protected rebuildUserGroupOptions(): void {
    this.availableUserGroupOptions = this.newUserGroups.map(group => ({
      id: group,
      name: group,
      selected: this.newConfigurations?.['NewUserDefaultsConfig']?.defaultActiveUserGroups?.includes(group) ?? false
    }));
  }

  public onSave(): void {
    if (Object.keys(this.newConfigurations).length === 0) {
      return;
    }

    this.checkForChanges();

    const subscriptions: Observable<any>[] = [];

    for (const configKey in this.newConfigurations) {
      if (this.newConfigurations.hasOwnProperty(configKey)) {
        const config = this.newConfigurations[configKey];
        subscriptions.push(this.configurationsService.updateConfiguration(config).pipe(first()));
      }
    }

    if (this.userGroupsChanged()) {
      subscriptions.push(this.saveUserGroups());
    }

    subscriptions.push(...this.getAdditionalSaveObservables());

    forkJoin(subscriptions).pipe(first()).subscribe({
      next: () => {
        this.unsavedChanges = false;
        this.gungNotificationService.notify('CHANGES_SAVED', '', 'success');
      },
      error: error => this.gungNotificationService.notify('ERROR', error?.error?.gungMessage, 'warning')
    });
  }

  protected userGroupsChanged(): boolean {
    return !checkObjectEquals(this.systemSettings?.extra?.userGroups ?? [], this.newUserGroups);
  }

  protected saveUserGroups(): Observable<SystemSettings> {
    const updated: SystemSettings = structuredClone(this.systemSettings);
    updated.extra = { ...(updated.extra ?? {}), userGroups: [...this.newUserGroups] };
    return this.gungFlowService.updateSystemGung(updated).pipe(
      first(),
      tap(saved => { this.systemSettings = saved; })
    );
  }

  /** Extension point for subclasses to persist extra settings within the same save. */
  protected getAdditionalSaveObservables(): Observable<any>[] {
    return [];
  }

  protected checkForChanges(): void {
    const configsEqual = checkObjectEquals(this.configurations, this.newConfigurations);
    this.unsavedChanges = !(configsEqual && !this.userGroupsChanged());
  }

  public addMultistockId(ele: HTMLInputElement): void {
    if (!ele.value) {
      return;
    }
    this.newConfigurations?.['NewUserDefaultsConfig']?.defaultMultistockIds.push(ele.value);
    ele.value = '';
    this.checkForChanges();
  }

  public removeMultistockId(index: number): void {
    this.newConfigurations?.['NewUserDefaultsConfig']?.defaultMultistockIds.splice(index, 1);
    this.checkForChanges();
  }

  public addUserGroup(ele: HTMLInputElement): void {
    const value = ele.value?.trim();
    ele.value = '';
    if (!value || this.newUserGroups.includes(value)) {
      return;
    }
    this.newUserGroups.push(value);
    this.rebuildUserGroupOptions();
    this.checkForChanges();
  }

  public removeUserGroup(index: number): void {
    this.newUserGroups.splice(index, 1);
    this.rebuildUserGroupOptions();
    this.checkForChanges();
  }

  public setSelectedUserGroupValues(values: string[]): void {
    this.newConfigurations['NewUserDefaultsConfig'].defaultActiveUserGroups = values;
    this.checkForChanges();
  }
}